import { cn } from '@today/ui-primitives'
import type { MouseEvent, ReactNode } from 'react'

/** One entry of the admin shell's section menu, already filtered to what the viewer may open. */
export interface AdminShellNavItem {
	key: string
	label: string
	href: string
	/** True for the section whose page is shown. */
	current: boolean
}

/**
 * Props for {@link AdminShellView}.
 *
 * @property sections - The visible sections, in menu order. Nothing else is rendered.
 * @property onNavigate - Called with a section's `href` on a plain left click (client-side routing).
 * @property children - The main area's content.
 */
export interface AdminShellViewProps {
	sections: AdminShellNavItem[]
	onNavigate: (href: string) => void
	children: ReactNode
}

/** A plain left click is routed in-app; modified clicks keep the browser default (new tab, …). */
const isPlainClick = (event: MouseEvent) =>
	event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey

/**
 * The admin shell frame (TO-189): the section menu beside the main area. Pure —
 * which sections the viewer may open is decided by the caller. Entries are real
 * links, so they can be opened in a new tab.
 */
export function AdminShellView({ sections, onNavigate, children }: AdminShellViewProps) {
	return (
		<div className="flex gap-6 p-6">
			<nav aria-label="Admin sections" className="w-56 shrink-0">
				<ul className="flex flex-col gap-1">
					{sections.map((section) => (
						<li key={section.key}>
							<a
								href={section.href}
								aria-current={section.current ? 'page' : undefined}
								onClick={(event) => {
									if (!isPlainClick(event)) return
									event.preventDefault()
									onNavigate(section.href)
								}}
								className={cn(
									'block rounded-md px-3 py-2 text-ds-sm',
									section.current ? 'bg-ds-surface-muted font-semibold text-ds-text' : 'text-ds-text-muted',
								)}
							>
								{section.label}
							</a>
						</li>
					))}
				</ul>
			</nav>
			<section className="min-w-0 flex-1">{children}</section>
		</div>
	)
}
